import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';

const CEG_OPTIONS = [
    'Children and Young People',
    'Gender, Reproductive & Sexual Health',
    'Long-term Conditions',
    'Older Adults and Frailty',
    'Mental Health and Addictions',
    'Urgent and Unscheduled Care',
    'Health Disadvantage and Vulnerabilities',
    'Ethnicity, Culture, Diversity & Inclusion',
    'Undifferentiated Presentations',
    'Safe Prescribing',
    'Data Interpretation and Results',
    'Professional Dilemmas'
];

const SCASetup = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const libraryId = searchParams.get('libraryId');
    const { user } = useAuth();

    const [candidateMaterial, setCandidateMaterial] = useState('');
    const [patientProfile, setPatientProfile] = useState('');
    const [markingGuide, setMarkingGuide] = useState({
        essential_history: [],
        management_musts: [],
        domain3_hooks: [],
        expected_diagnosis: '',
        case_complexity_notes: ''
    });
    const [ceg, setCeg] = useState(CEG_OPTIONS[0]);
    const [difficulty, setDifficulty] = useState('intermediate');
    const [authorName, setAuthorName] = useState(user?.name || '');
    const [saveToLibrary, setSaveToLibrary] = useState(false);

    const [tags, setTags] = useState([]);
    const [selectedTags, setSelectedTags] = useState([]);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [scenarioTopic, setScenarioTopic] = useState('');

    useEffect(() => {
        api.getTags().then(res => setTags(res.data.tags)).catch(console.error);

        if (libraryId) {
            setLoading(true);
            api.getSCAScenario(libraryId)
                .then(res => {
                    const data = res.data;
                    setCandidateMaterial(data.candidate_material);
                    setPatientProfile(data.patient_profile);
                    setMarkingGuide(data.marking_guide);
                    setCeg(data.clinical_experience_group);
                    setDifficulty(data.difficulty);
                    if (data.tags) {
                        setSelectedTags(data.tags.map(t => t.id));
                    }
                })
                .catch(err => toast.error("Failed to load scenario"))
                .finally(() => setLoading(false));
        }
    }, [libraryId]);

    const handleGenerateScenario = async () => {
        if (!scenarioTopic.trim()) {
            toast.error("Please enter a topic");
            return;
        }
        setGenerating(true);

        try {
            const res = await api.post('/api/simulator/generate-sca-scenario', { topic: scenarioTopic }, { timeout: 120000 });
            const data = res.data;

            setCandidateMaterial(data.candidate_material);
            setPatientProfile(data.patient_profile);
            if (data.marking_guide) setMarkingGuide(data.marking_guide);
            if (data.clinical_experience_group) setCeg(data.clinical_experience_group);
            if (data.difficulty) setDifficulty(data.difficulty.toLowerCase());

            toast.success("Scenario generated!");
        } catch (err) {
            console.error("Generation failed:", err);
            toast.error("Failed to generate scenario");
        } finally {
            setGenerating(false);
        }
    };

    const handleStart = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const scenarioData = {
                candidate_material: candidateMaterial,
                patient_profile: patientProfile,
                marking_guide: markingGuide,
                clinical_experience_group: ceg,
                difficulty: difficulty,
                tag_ids: selectedTags,
                author_name: authorName,
                is_library_item: saveToLibrary
            };

            const scenarioRes = await api.createSCAScenario(scenarioData);
            const scenarioId = scenarioRes.data.id;

            const sessionRes = await api.createSCASession(scenarioId);
            const sessionId = sessionRes.data.id;

            navigate(`/sca/simulation/${sessionId}`);
        } catch (err) {
            console.error(err);
            toast.error("Failed to start SCA simulation");
        } finally {
            setLoading(false);
        }
    };

    const updateMarkingGuide = (field, value) => {
        setMarkingGuide(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const [showTagInput, setShowTagInput] = useState(false);
    const [newTagName, setNewTagName] = useState('');
    const [newTagCategory, setNewTagCategory] = useState('objective');

    const handleCreateTag = async () => {
        if (!newTagName.trim()) return;
        try {
            const res = await api.createTag({ name: newTagName, category: newTagCategory });
            setTags(prev => [...prev, res.data]);
            setNewTagName('');
            setShowTagInput(false);
            toast.success("Tag created successfully");
        } catch (err) {
            console.error("Failed to create tag", err);
            toast.error("Failed to create tag");
        }
    };

    const handleListUpdate = (field, value) => {
        const items = value.split('\n').filter(i => i.trim() !== '');
        updateMarkingGuide(field, items);
    };

    return (
        <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '20px' }}>
            <h1 className="text-primary">SCA Practice Setup</h1>
            <p className="text-muted">Prepare an RCGP-style consultation assessment.</p>

            <div className="card-bg" style={{ padding: '20px', borderRadius: '12px', border: '1px solid var(--border-color)', marginBottom: '30px', background: '#f8f9fa' }}>
                <h3 style={{ marginTop: 0 }}>✨ AI Scenario Generator</h3>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <input
                        type="text"
                        className="input-bg"
                        style={{ flex: 1, padding: '10px', borderRadius: '4px' }}
                        placeholder="Enter a topic (e.g. 'Asthma review for 45yo man' or 'Palliative care discussion')"
                        value={scenarioTopic}
                        onChange={(e) => setScenarioTopic(e.target.value)}
                    />
                    <button
                        type="button"
                        onClick={handleGenerateScenario}
                        disabled={generating}
                        className="btn btn-secondary"
                        style={{ padding: '10px 20px', background: generating ? '#ccc' : '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                        {generating ? 'Generating...' : 'Generate full scenario'}
                    </button>
                </div>
            </div>

            <form onSubmit={handleStart} style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
                <div className="card-bg" style={{ padding: '20px', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
                    <h3 style={{ marginTop: 0 }}>Step 1: Candidate Material</h3>
                    <p style={{ fontSize: '14px', color: '#666' }}>This is the pre-reading material shown to the trainee for 3 minutes before starting.</p>
                    <ReactQuill theme="snow" value={candidateMaterial} onChange={setCandidateMaterial} style={{ height: '200px', marginBottom: '50px' }} />
                </div>

                <div className="card-bg" style={{ padding: '20px', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <h3 style={{ margin: 0 }}>Step 2: Patient Profile (Roleplay)</h3>
                    </div>
                    <p style={{ fontSize: '14px', color: '#666' }}>The full persona for the LLM. Describe personality, hidden concerns, and symptoms.</p>
                    <ReactQuill theme="snow" value={patientProfile} onChange={setPatientProfile} style={{ height: '300px', marginBottom: '50px' }} />
                </div>

                <div className="card-bg" style={{ padding: '20px', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
                    <h3 style={{ marginTop: 0 }}>Step 3: Marking Guide</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        <div>
                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Essential History (one per line)</label>
                            <textarea
                                className="input-bg"
                                style={{ width: '100%', height: '100px', padding: '10px', borderRadius: '4px' }}
                                value={markingGuide.essential_history.join('\n')}
                                onChange={(e) => handleListUpdate('essential_history', e.target.value)}
                            />
                        </div>
                        <div>
                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Management Musts (one per line)</label>
                            <textarea
                                className="input-bg"
                                style={{ width: '100%', height: '100px', padding: '10px', borderRadius: '4px' }}
                                value={markingGuide.management_musts.join('\n')}
                                onChange={(e) => handleListUpdate('management_musts', e.target.value)}
                            />
                        </div>
                        <div>
                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Domain 3 Hooks (Hidden concerns)</label>
                            <textarea
                                className="input-bg"
                                style={{ width: '100%', height: '100px', padding: '10px', borderRadius: '4px' }}
                                value={markingGuide.domain3_hooks.join('\n')}
                                onChange={(e) => handleListUpdate('domain3_hooks', e.target.value)}
                            />
                        </div>
                        <div>
                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Expected Diagnosis</label>
                            <input
                                className="input-bg"
                                type="text"
                                style={{ width: '100%', padding: '10px', borderRadius: '4px' }}
                                value={markingGuide.expected_diagnosis}
                                onChange={(e) => updateMarkingGuide('expected_diagnosis', e.target.value)}
                            />
                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px', marginTop: '10px' }}>Case Complexity Notes</label>
                            <input
                                className="input-bg"
                                type="text"
                                style={{ width: '100%', padding: '10px', borderRadius: '4px' }}
                                value={markingGuide.case_complexity_notes}
                                onChange={(e) => updateMarkingGuide('case_complexity_notes', e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                <div className="card-bg" style={{ padding: '20px', borderRadius: '12px', border: '1px solid var(--border-color)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <div>
                        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Clinical Experience Group</label>
                        <select
                            className="input-bg"
                            style={{ width: '100%', padding: '10px', borderRadius: '4px' }}
                            value={ceg}
                            onChange={(e) => setCeg(e.target.value)}
                        >
                            {CEG_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                    </div>
                    <div>
                        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Difficulty</label>
                        <select
                            className="input-bg"
                            style={{ width: '100%', padding: '10px', borderRadius: '4px' }}
                            value={difficulty}
                            onChange={(e) => setDifficulty(e.target.value)}
                        >
                            <option value="easy">Easy</option>
                            <option value="intermediate">Intermediate</option>
                            <option value="hard">Hard</option>
                        </select>
                    </div>
                </div>

                <div className="secondary-bg" style={{ padding: '20px', borderRadius: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: '15px' }}>
                        <input
                            type="checkbox"
                            checked={saveToLibrary}
                            onChange={(e) => setSaveToLibrary(e.target.checked)}
                            style={{ width: '20px', height: '20px', marginRight: '10px' }}
                        />
                        <span style={{ fontSize: '16px', fontWeight: 'bold' }}>Save this SCA scenario to the Library</span>
                    </label>

                    {saveToLibrary && (
                        <div style={{ marginLeft: '30px' }}>
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px' }}>Author Name</label>
                                <input
                                    type="text"
                                    value={authorName}
                                    onChange={(e) => setAuthorName(e.target.value)}
                                    placeholder="e.g. Dr. House"
                                    className="input-bg"
                                    style={{ padding: '8px', width: '100%', maxWidth: '300px', borderRadius: '4px' }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '5px' }}>Tags</label>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                                    {tags.map(tag => (
                                        <button
                                            key={tag.id}
                                            type="button"
                                            onClick={() => setSelectedTags(prev => prev.includes(tag.id) ? prev.filter(t => t !== tag.id) : [...prev, tag.id])}
                                            className={selectedTags.includes(tag.id) ? 'btn-primary' : 'tag-default'}
                                            style={{
                                                padding: '5px 12px',
                                                borderRadius: '15px',
                                                border: '1px solid var(--border-color)',
                                                background: selectedTags.includes(tag.id) ? '#0070f3' : 'transparent',
                                                color: selectedTags.includes(tag.id) ? 'white' : 'inherit',
                                                cursor: 'pointer',
                                                fontSize: '12px'
                                            }}
                                        >
                                            {tag.name}
                                        </button>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={() => setShowTagInput(!showTagInput)}
                                        style={{
                                            padding: '5px 12px',
                                            borderRadius: '15px',
                                            border: '1px dashed #666',
                                            background: 'transparent',
                                            color: '#666',
                                            cursor: 'pointer',
                                            fontSize: '12px'
                                        }}
                                    >
                                        + New Tag
                                    </button>
                                </div>

                                {showTagInput && (
                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', background: '#f8f9fa', padding: '10px', borderRadius: '8px' }}>
                                        <input
                                            type="text"
                                            placeholder="Tag name"
                                            value={newTagName}
                                            onChange={(e) => setNewTagName(e.target.value)}
                                            className="input-bg"
                                            style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
                                        />
                                        <select
                                            value={newTagCategory}
                                            onChange={(e) => setNewTagCategory(e.target.value)}
                                            style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
                                        >
                                            <option value="objective">Objective</option>
                                            <option value="difficulty">Difficulty</option>
                                            <option value="system">System</option>
                                        </select>
                                        <button
                                            type="button"
                                            onClick={handleCreateTag}
                                            disabled={!newTagName.trim()}
                                            style={{ padding: '5px 10px', background: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                        >
                                            Add
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '50px' }}>
                    <button
                        type="submit"
                        disabled={loading || generating}
                        className="btn btn-primary"
                        style={{ padding: '15px 40px', fontSize: '18px', background: '#0070f3', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', opacity: (loading || generating) ? 0.7 : 1 }}
                    >
                        {loading ? 'Initializing...' : 'Start SCA Session'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default SCASetup;
